import React, { useState } from 'react';
import { Box, Button, Header, Link, Pagination, Popover, StatusIndicator } from '@amzn/awsui-components-react';
import Table, { TableProps } from '@amzn/awsui-components-react/polaris/table';
import PropertyFilter from '@amzn/awsui-components-react/polaris/property-filter';
import { useCollection } from '@amzn/awsui-collection-hooks';
import CONSTANTS from 'src/utils/constants';
import StringUtils from 'src/utils/stringUtils';
import {
    PromotionRecord,
    PromotionStageInfo,
    PromotionStatus,
    getPromotionTargetWebsiteUrl,
} from 'src/models/promotion/PromotionTypes';
import {
    PROMOTION_HISTORY_COLUMN_DEFINITIONS,
    PROMOTION_HISTORY_FILTERING_PROPERTIES,
    PROMOTION_HISTORY_PAGE_SIZE_OPTIONS,
    PROMOTION_HISTORY_VISIBLE_CONTENT_OPTIONS,
    PROMOTION_STATUS_INDICATOR_CONFIG,
} from './promotionHistoryConstants';
import {
    PROPERTY_FILTERING_I18N_CONSTANTS,
    PAGINATION_LABELS,
    getFilterCounterText,
    getTablePreferences,
} from '../shared/TPPolarisTable';
import './PromotionHistory.scss';

export interface PromotionHistoryRow extends PromotionRecord {
    promotedAtDisplay: string;
    componentNameDisplay: string;
    promotedByDisplay: string;
    statusDisplay: string;
}

function getStatusIndicator(promotionRecord: PromotionHistoryRow, targetName: string) {
    const config = PROMOTION_STATUS_INDICATOR_CONFIG[promotionRecord.status];
    if (!config) return promotionRecord.status;
    const [label, statusType] = config;
    const indicator = <StatusIndicator type={statusType}>{label}</StatusIndicator>;
    if (promotionRecord.status === PromotionStatus.PENDING_SUBMISSION_REQUIRES_ACTION) {
        return (
            <Popover
                dismissButton={false}
                position="top"
                size="medium"
                triggerType="text"
                content={`Promotion successful but submission for approval was unsuccessful in ${targetName}`}
            >
                {indicator}
            </Popover>
        );
    }

    if (promotionRecord.status === PromotionStatus.FAILED) {
        return (
            <Popover
                dismissButton={false}
                position="top"
                size="large"
                triggerType="text"
                content={promotionRecord.errorMessage || 'No error details available.'}
            >
                {indicator}
            </Popover>
        );
    }

    return indicator;
}

interface PromotionHistoryTableProps {
    rows: PromotionHistoryRow[];
    isLoading: boolean;
    stageInfo: PromotionStageInfo | null;
    hasNextPage: boolean;
    currentPageIndex: number;
    pagesCount: number;
    onNextPage: () => void;
    onPreviousPage: () => void;
    onPageChange: (pageIndex: number) => void;
    onRefresh: () => void;
    preferences: any;
    onPreferencesChange: (prefs: any) => void;
}

export default function PromotionHistoryTable(props: PromotionHistoryTableProps) {
    const {
        rows, isLoading, stageInfo,
        hasNextPage, currentPageIndex, pagesCount,
        onNextPage, onPreviousPage, onPageChange, onRefresh,
        preferences, onPreferencesChange,
    } = props;

    const targetBaseUrl = getPromotionTargetWebsiteUrl(CONSTANTS.ENVIRONMENT_VARIABLES.TPE_UI_STAGE);
    const [expandedItems, setExpandedItems] = useState<PromotionHistoryRow[]>([]);

    const columnDefinitions: TableProps.ColumnDefinition<PromotionHistoryRow>[] = [{
        ...PROMOTION_HISTORY_COLUMN_DEFINITIONS.TIMESTAMP,
        cell: item => item.promotedAtDisplay,
        isRowHeader: true,
    }, {
        ...PROMOTION_HISTORY_COLUMN_DEFINITIONS.STATUS,
        cell: item => getStatusIndicator(item, stageInfo?.to || 'target'),
    }, {
        ...PROMOTION_HISTORY_COLUMN_DEFINITIONS.COMPONENT_TYPE,
        cell: item => item.componentType,
    }, {
        ...PROMOTION_HISTORY_COLUMN_DEFINITIONS.COMPONENT_NAME,
        cell: item => item.componentNameDisplay,
    }, {
        ...PROMOTION_HISTORY_COLUMN_DEFINITIONS.SOURCE,
        cell: item => StringUtils.isNullOrEmpty(item.navigationPath)
            ? 'N/A'
            : <Link href={item.navigationPath} target="_blank">View in {stageInfo?.from || 'Source'}</Link>,
    }, {
        ...PROMOTION_HISTORY_COLUMN_DEFINITIONS.TARGET,
        cell: item => (StringUtils.isNullOrEmpty(item.navigationPath) || !targetBaseUrl)
            ? 'N/A'
            : <Link href={`${targetBaseUrl}${item.navigationPath}`} target="_blank">View in {stageInfo?.to || 'Target'}</Link>,
    }, {
        ...PROMOTION_HISTORY_COLUMN_DEFINITIONS.PERIOD,
        cell: item => item.accountingPeriod || '',
    }, {
        ...PROMOTION_HISTORY_COLUMN_DEFINITIONS.PROMOTED_BY,
        cell: item => item.promotedByDisplay,
    }];

    // Expandable rows: rows with selectedRows get an expand arrow.
    // The child row is a single synthetic item whose first-cell content spans
    // visually across the full width (via CSS overflow:visible on the td).
    const expandableRows: TableProps.ExpandableRows<PromotionHistoryRow> = {
        getItemChildren: (item) => {
            if (!item.selectedRows || item.selectedRows.length === 0) return [];
            return [{
                ...item,
                promotionId: `${item.promotionId}__rows`,
                promotedAtDisplay: '__PROMOTED_ROWS__',
            }];
        },
        isItemExpandable: (item) =>
            !item.promotionId.endsWith('__rows') && !!item.selectedRows && item.selectedRows.length > 0,
        expandedItems,
        onExpandableItemToggle: ({ detail }) => {
            setExpandedItems(prev => {
                const next = new Set(prev);
                detail.expanded ? next.add(detail.item) : next.delete(detail.item);
                return [...next];
            });
        },
    };

    // Override the Timestamp column cell for the child row to render the full-width detail
    columnDefinitions[0] = {
        ...columnDefinitions[0],
        cell: (item) => {
            if (item.promotedAtDisplay !== '__PROMOTED_ROWS__') return item.promotedAtDisplay;
            return (
                <div className="promotedRowsDetail">
                    <Box color="text-status-success" fontWeight="bold" fontSize="body-s">
                        Promoted rows ({item.selectedRows!.length}):
                    </Box>
                    <div style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                        {item.selectedRows!.map((row, i) => <div key={i}>{row}</div>)}
                    </div>
                </div>
            );
        },
    };

    // Other columns render nothing for the child row
    for (let i = 1; i < columnDefinitions.length; i++) {
        const original = columnDefinitions[i].cell;
        columnDefinitions[i] = {
            ...columnDefinitions[i],
            cell: (item) => item.promotedAtDisplay === '__PROMOTED_ROWS__' ? null : (original as any)(item),
        };
    }

    const {
        items,
        collectionProps,
        propertyFilterProps,
        filteredItemsCount,
    } = useCollection(rows, {
        propertyFiltering: {
            filteringProperties: PROMOTION_HISTORY_FILTERING_PROPERTIES,
        },
        sorting: {
            defaultState: {
                sortingColumn: { sortingField: PROMOTION_HISTORY_COLUMN_DEFINITIONS.TIMESTAMP.sortingField } as any,
                isDescending: true,
            },
        },
    });

    return (
        <Table
            {...collectionProps}
            columnDefinitions={columnDefinitions}
            items={items}
            header={
                <Header
                    actions={
                        <Button
                            iconName="refresh"
                            variant="normal"
                            loading={isLoading}
                            onClick={onRefresh}
                            ariaLabel="Refresh promotion history"
                        >Refresh</Button>
                    }
                >Promotion history</Header>
            }
            loadingText="Loading promotion history"
            loading={isLoading}
            resizableColumns={true}
            trackBy="promotionId"
            wrapLines={preferences.wrapLines}
            stripedRows={preferences.stripedRows}
            columnDisplay={preferences.contentDisplay}
            stickyColumns={preferences.stickyColumns}
            empty={<Box textAlign="center"><b>No promotions found for this period.</b></Box>}
            variant="container"
            expandableRows={expandableRows}
            filter={
                <PropertyFilter
                    i18nStrings={{ ...PROPERTY_FILTERING_I18N_CONSTANTS, filteringPlaceholder: 'Find resources' }}
                    {...propertyFilterProps}
                    countText={getFilterCounterText(filteredItemsCount)}
                    filteringEmpty="No promotions match the filter criteria"
                    filteringLoadingText="Loading promotion history..."
                />
            }
            pagination={
                <Pagination
                    currentPageIndex={currentPageIndex}
                    pagesCount={pagesCount}
                    openEnd={hasNextPage}
                    ariaLabels={PAGINATION_LABELS}
                    onNextPageClick={({ detail }) => {
                        if (detail.requestedPageIndex > pagesCount && hasNextPage) {
                            onNextPage();
                        } else {
                            onPageChange(detail.requestedPageIndex);
                        }
                    }}
                    onPreviousPageClick={({ detail }) => {
                        onPreviousPage();
                        onPageChange(detail.requestedPageIndex);
                    }}
                    onChange={({ detail }) => onPageChange(detail.currentPageIndex)}
                />
            }
            preferences={
                getTablePreferences(
                    'Preferences',
                    false,
                    PROMOTION_HISTORY_PAGE_SIZE_OPTIONS,
                    PROMOTION_HISTORY_VISIBLE_CONTENT_OPTIONS,
                    preferences,
                    onPreferencesChange,
                )
            }
        />
    );
}
